import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { getProfileAssetUrl, resolveUploadProfileDisplayUrl } from '../utils/api';
import { prepareFileForUpload } from '../utils/imageUploadCompress';
import { todayString, formatDate, formatMonthYear } from '../utils/appDateTime';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  IndianRupee,
  CreditCard,
  ClipboardList,
  User,
  FileText,
  Camera,
  Calendar,
  Save,
  Edit,
  Users,
  Send,
  IdCard,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import StudentIdCard from './StudentIdCard';
import { openFeeReceiptPortal } from '../utils/documentPortalUrl';
import { isSchoolFeatureEnabled, getSchoolFeatures } from '../utils/schoolFeatures';
import StudentFacilityToggles from './StudentFacilityToggles';
import StudentAdmissionSections from './students/StudentAdmissionSections';
import {
  createEmptyPendingUploads,
  deleteStudentDocument,
  uploadAllAdmissionAssets,
  validateAadharNumber,
} from '../utils/studentAdmissionUploads';
import MobileInput from './ui/mobile-input';
import DateInput from './ui/date-input';
import { validateMobileFields } from '../utils/phoneValidation';

/** @param {Record<string, unknown>|undefined|null} cls */
const classUsesSections = (cls) => {
  if (!cls) return true;
  if (cls.uses_sections === undefined || cls.uses_sections === null) return true;
  return Number(cls.uses_sections) === 1 || cls.uses_sections === true;
};

const StudentProfile = ({ editMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canManageFeature, canViewFeature } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [feeMonths, setFeeMonths] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [phoneErrors, setPhoneErrors] = useState({});
  const [formData, setFormData] = useState(null);
  const [feeStructure, setFeeStructure] = useState([]);
  const [payFeeDialogOpen, setPayFeeDialogOpen] = useState(false);
  const [payFeeForm, setPayFeeForm] = useState({
    student_id: '',
    amount: '',
    discount: '0',
    payment_method: 'cash',
    remarks: '',
    month_label: '',
    fee_month: '', // YYYY-MM for which month the fee is for
    payment_date: todayString(), // Date when payment was made (can be backdated)
  });
  const [resendingCreds, setResendingCreds] = useState(false);
  const [admissionNotifLogs, setAdmissionNotifLogs] = useState([]);
  const [admissionNotifLoading, setAdmissionNotifLoading] = useState(false);
  const [resendingAdmission, setResendingAdmission] = useState(false);
  const [resendingGuardian, setResendingGuardian] = useState(false);
  const [pendingUploads, setPendingUploads] = useState(createEmptyPendingUploads);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const describeEmailNotifications = (en) => {
    if (!en) return '';
    const parts = [];
    if (en.sent > 0) parts.push(`${en.sent} email(s) sent`);
    if (en.queued > 0) parts.push(`${en.queued} queued`);
    if (en.failed > 0) parts.push(`${en.failed} failed`);
    if (en.skipped?.length) {
      const reasons = en.skipped.map((s) => `${s.recipient}: ${s.reason}`).join('; ');
      parts.push(`skipped (${reasons})`);
    }
    return parts.join('. ');
  };

  const postResendAdmissionEmails = async (recipient = null) => {
    if (!id) return null;
    const params = recipient ? { recipient } : {};
    const res = await api.post(`/students/${id}/resend-admission-emails`, null, { params });
    return res.data;
  };
  const [profileTab, setProfileTab] = useState('overview');
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [photoCacheKey, setPhotoCacheKey] = useState('');
  const photoInputRef = React.useRef(null);

  const canManageStudents = canManageFeature('students');
  const isAdmin = user?.role_slug === 'school_admin';

  const selectedClassForForm = useMemo(
    () => classes.find((c) => String(c.id) === String(formData?.class_id ?? '')),
    [classes, formData?.class_id],
  );
  const editClassUsesSections = useMemo(() => classUsesSections(selectedClassForForm), [selectedClassForForm]);

  const handleResendCredentials = async () => {
    if (!id) return;
    setResendingCreds(true);
    try {
      const data = await postResendAdmissionEmails();
      const en = data?.data?.email_notifications;
      const detail = describeEmailNotifications(en);
      const msg = data?.message || (detail ? `Credentials resent. ${detail}` : 'New temporary password sent to student and guardian.');
      const allSkipped = en && en.queued === 0 && (en.sent ?? 0) === 0 && (en.skipped?.length || 0) > 0;
      toast({
        title: allSkipped ? 'Resend failed' : 'Credentials sent',
        description: msg,
        variant: allSkipped ? 'destructive' : 'default',
      });
      fetchAdmissionNotificationLogs();
    } catch (err) {
      toast({
        title: 'Failed',
        description: err.response?.data?.message || 'Could not resend credentials',
        variant: 'destructive',
      });
    } finally {
      setResendingCreds(false);
    }
  };

  useEffect(() => {
    if (id) fetchProfile();
  }, [id, user?.school_features?.is_library_enabled, user?.school_features?.is_transport_enabled]);

  const fetchAdmissionNotificationLogs = useCallback(async () => {
    if (!id || !canManageStudents) return;
    setAdmissionNotifLoading(true);
    try {
      const res = await api.get(`/students/${id}/notification-logs`);
      setAdmissionNotifLogs(res.data?.data?.admission_logs || []);
    } catch {
      setAdmissionNotifLogs([]);
    } finally {
      setAdmissionNotifLoading(false);
    }
  }, [id, canManageStudents]);

  useEffect(() => {
    if (id && canManageStudents && !editMode) {
      fetchAdmissionNotificationLogs();
    }
  }, [id, canManageStudents, editMode, fetchAdmissionNotificationLogs]);

  const handleResendAdmissionEmails = async () => {
    if (!id) return;
    setResendingAdmission(true);
    try {
      const data = await postResendAdmissionEmails();
      const en = data?.data?.email_notifications;
      const detail = describeEmailNotifications(en);
      const msg = data?.message || (detail ? `Resent. ${detail}` : 'Admission emails sent with a new temporary password.');
      const allSkipped = en && en.queued === 0 && (en.sent ?? 0) === 0 && (en.skipped?.length || 0) > 0;
      toast({
        title: allSkipped ? 'Resend failed' : 'Sent',
        description: msg,
        variant: allSkipped ? 'destructive' : 'default',
      });
      fetchAdmissionNotificationLogs();
    } catch (err) {
      toast({
        title: 'Failed',
        description: err.response?.data?.message || 'Could not resend admission emails',
        variant: 'destructive',
      });
    } finally {
      setResendingAdmission(false);
    }
  };

  const handleResendGuardianEmails = async () => {
    if (!id) return;
    setResendingGuardian(true);
    try {
      const data = await postResendAdmissionEmails('guardian');
      const en = data?.data?.email_notifications;
      const detail = describeEmailNotifications(en);
      const msg = data?.message || (detail ? `Guardian resent. ${detail}` : 'Guardian credential email sent.');
      const allSkipped = en && en.queued === 0 && (en.sent ?? 0) === 0 && (en.skipped?.length || 0) > 0;
      toast({
        title: allSkipped ? 'Resend failed' : 'Sent to guardian',
        description: msg,
        variant: allSkipped ? 'destructive' : 'default',
      });
      fetchAdmissionNotificationLogs();
    } catch (err) {
      toast({
        title: 'Failed',
        description: err.response?.data?.message || 'Could not resend to guardian',
        variant: 'destructive',
      });
    } finally {
      setResendingGuardian(false);
    }
  };

  const fetchSchoolInfo = React.useCallback((schoolId) => {
    if (!schoolId) {
      setSchoolInfo(null);
      return Promise.resolve();
    }
    return api
      .get(`schools/${schoolId}`)
      .then((res) => {
        setSchoolInfo(res.data?.data ?? null);
      })
      .catch(() => {
        setSchoolInfo(null);
      });
  }, []);

  useEffect(() => {
    const sid = profile?.student?.school_id || user?.school_id;
    fetchSchoolInfo(sid);
  }, [profile?.student?.school_id, user?.school_id, fetchSchoolInfo]);

  useEffect(() => {
    if (profileTab !== 'id') return;
    const sid = profile?.student?.school_id || user?.school_id;
    if (sid) fetchSchoolInfo(sid);
  }, [profileTab, profile?.student?.school_id, user?.school_id, fetchSchoolInfo]);

  const canViewFees = canViewFeature('fees');

  useEffect(() => {
    if (!id || !canViewFees) return;
    api.get(`fees/student/${id}/months`)
      .then((res) => setFeeMonths(res.data.data?.months ?? []))
      .catch(() => setFeeMonths([]));
  }, [id, canViewFees]);

  useEffect(() => {
    if (!canViewFees || !profile?.student?.class_id || !id) return;
    const params = { class_id: profile.student.class_id, student_id: id };
    if (user?.school_id != null) params.school_id = user.school_id;
    api.get('/fees/structure', { params })
      .then((res) => setFeeStructure(res.data.data || []))
      .catch(() => setFeeStructure([]));
  }, [canViewFees, profile?.student?.class_id, user?.school_id, id, profile?.uses_library, profile?.uses_transport]);

  const reloadProfile = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`students/${id}/profile`);
      setProfile(res.data.data || null);
    } catch {
      /* keep existing */
    }
  }, [id]);

  const handleFacilityToggle = async (field, checked) => {
    if (!id || !canManageStudents) return;
    try {
      await api.patch(`students/${id}/facilities`, { [field]: checked });
      await reloadProfile();
      const params = { class_id: profile?.student?.class_id, student_id: id };
      if (user?.school_id != null) params.school_id = user.school_id;
      const structRes = await api.get('/fees/structure', { params });
      setFeeStructure(structRes.data.data || []);
      toast({
        title: 'Updated',
        description: 'Facility setting saved. Fee totals updated.',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update facility',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (!editMode) return;
    api.get('classes')
      .then((res) => setClasses(res.data.data || []))
      .catch(() => setClasses([]));
  }, [editMode]);

  useEffect(() => {
    if (!editMode || !formData?.class_id || !editClassUsesSections) {
      setSections([]);
      return;
    }
    api.get(`classes/${formData.class_id}/sections`)
      .then((res) => setSections(res.data.data || []))
      .catch(() => setSections([]));
  }, [editMode, formData?.class_id, editClassUsesSections]);

  // When navigating from profile view to edit (same id), profile is already loaded but formData was never set. Sync formData from profile.
  useEffect(() => {
    if (!editMode || !profile?.student) return;
    setFormData((prev) => {
      if (prev != null) return prev;
      const s = profile.student;
      return {
        first_name: s.first_name || '',
        last_name: s.last_name || '',
        phone: s.phone || '',
        class_id: s.class_id?.toString() || '',
        section_id: s.section_id?.toString() || '',
        roll_no: s.roll_no || '',
        date_of_birth: (s.date_of_birth || '').toString().slice(0, 10),
        admission_date: (s.admission_date || '').toString().slice(0, 10),
        gender: s.gender || '',
        blood_group: s.blood_group || '',
        address: s.address || '',
        city: s.city || '',
        state: s.state || '',
        pincode: s.pincode || '',
        aadhar_number: s.aadhar_number || '',
        guardian_aadhar_number: s.guardian_aadhar_number || '',
        status: s.status || 'active',
        guardian_name: s.guardian_name || '',
        guardian_phone: s.guardian_phone || '',
        guardian_email: s.guardian_email || '',
        uses_library: !!profile?.uses_library,
        uses_transport: !!profile?.uses_transport,
      };
    });
  }, [editMode, profile]);

  const fetchProfile = async () => {
    try {
      const response = await api.get(`students/${id}/profile`);
      const data = response.data.data;
      setProfile(data);
      if (editMode && data?.student) {
        const s = data.student;
        setFormData({
          first_name: s.first_name || '',
          last_name: s.last_name || '',
          phone: s.phone || '',
          class_id: s.class_id?.toString() || '',
          section_id: s.section_id?.toString() || '',
          roll_no: s.roll_no || '',
          date_of_birth: (s.date_of_birth || '').toString().slice(0, 10),
          admission_date: (s.admission_date || '').toString().slice(0, 10),
          gender: s.gender || '',
          blood_group: s.blood_group || '',
          address: s.address || '',
          city: s.city || '',
          state: s.state || '',
          pincode: s.pincode || '',
          aadhar_number: s.aadhar_number || '',
          guardian_aadhar_number: s.guardian_aadhar_number || '',
          status: s.status || 'active',
          guardian_name: s.guardian_name || '',
          guardian_phone: s.guardian_phone || '',
          guardian_email: s.guardian_email || '',
          uses_library: !!data?.uses_library,
          uses_transport: !!data?.uses_transport,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load student profile',
        variant: 'destructive',
      });
      navigate('/students');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData || !id) return;
    const errors = validateMobileFields({
      phone: formData.phone,
      guardian_phone: formData.guardian_phone,
    });
    if (Object.keys(errors).length) {
      setPhoneErrors(errors);
      toast({ title: 'Invalid mobile number', description: Object.values(errors)[0], variant: 'destructive' });
      return;
    }
    setPhoneErrors({});
    if (editClassUsesSections && !formData.section_id) {
      toast({ title: 'Section required', description: 'Select a section for this class.', variant: 'destructive' });
      return;
    }
    const aadharErr = validateAadharNumber(formData.aadhar_number, 'Student Aadhar')
      || validateAadharNumber(formData.guardian_aadhar_number, 'Guardian Aadhar');
    if (aadharErr) {
      toast({ title: 'Invalid Aadhar', description: aadharErr, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`students/${id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        class_id: formData.class_id ? parseInt(formData.class_id, 10) : null,
        section_id: editClassUsesSections && formData.section_id ? parseInt(formData.section_id, 10) : null,
        roll_no: formData.roll_no || null,
        date_of_birth: formData.date_of_birth || null,
        admission_date: formData.admission_date || null,
        gender: formData.gender || null,
        blood_group: formData.blood_group || null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        pincode: formData.pincode || null,
        aadhar_number: formData.aadhar_number?.trim() || null,
        guardian_aadhar_number: formData.guardian_aadhar_number?.trim() || null,
        status: formData.status || null,
        guardian_name: formData.guardian_name || null,
        guardian_phone: formData.guardian_phone || null,
        guardian_email: formData.guardian_email || null,
        uses_library: formData.uses_library ? 1 : 0,
        uses_transport: formData.uses_transport ? 1 : 0,
      });
      const pdata = res.data?.data;
      let desc = 'Student updated successfully';
      if (pdata?.parent_linked_existing) {
        desc = 'Student updated and linked to existing parent fee account.';
      } else if (pdata?.parent_account_id) {
        desc = 'Student updated and linked to parent fee account.';
      }
      const uploadResult = await uploadAllAdmissionAssets(id, pendingUploads);
      setPendingUploads(createEmptyPendingUploads());
      if (uploadResult.errors.length > 0) {
        toast({
          title: 'Saved — some uploads failed',
          description: uploadResult.errors.map((e) => `${e.field}: ${e.message}`).join('; '),
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Success', description: desc });
      }
      navigate(`/students/${id}`);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update student',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!id || !docId) return;
    setDeletingDocId(docId);
    try {
      await deleteStudentDocument(id, docId);
      await fetchProfile();
      toast({ title: 'Document removed', description: 'The file has been deleted.' });
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err.response?.data?.message || 'Could not delete document.',
        variant: 'destructive',
      });
    } finally {
      setDeletingDocId(null);
    }
  };

  const formatCurrency = (n) => {
    const num = Number(n);
    if (isNaN(num)) return '—';
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const calculateMonthlyFeeAmount = () => {
    if (!feeStructure || !feeStructure.length) return 0;
    // Sum all monthly fees (fees that recur every month)
    const monthlyFees = feeStructure
      .filter((fs) => fs.fee_type_frequency === 'monthly')
      .reduce((sum, fs) => sum + Number(fs.amount || 0), 0);
    return monthlyFees;
  };

  const handleOpenPayFee = async (month) => {
    if (!id || !profile?.student?.class_id) return;
    
    // Ensure fee structure is loaded
    let structure = feeStructure;
    if (!structure || structure.length === 0) {
      try {
        const params = { class_id: profile.student.class_id, student_id: id };
        if (user?.school_id != null) params.school_id = user.school_id;
        const res = await api.get('/fees/structure', { params });
        structure = res.data.data || [];
        setFeeStructure(structure);
      } catch (err) {
        console.error('Failed to load fee structure', err);
        structure = [];
      }
    }
    
    // Calculate amount from fee structure
    const monthlyAmount = structure
      .filter((fs) => fs.fee_type_frequency === 'monthly')
      .reduce((sum, fs) => sum + Number(fs.amount || 0), 0);
    
    setPayFeeForm({
      student_id: id,
      amount: monthlyAmount > 0 ? String(monthlyAmount) : '',
      discount: '0',
      payment_method: 'cash',
      remarks: `Fee for ${month.month_label}`,
      month_label: month.month_label,
      fee_month: month.year_month || '', // so backend marks this month as paid
      payment_date: todayString(), // Default to today, can be changed for backdating
    });
    setPayFeeDialogOpen(true);
  };

  const handlePayFee = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        student_id: payFeeForm.student_id,
        amount: payFeeForm.amount,
        discount: payFeeForm.discount || '0',
        payment_method: payFeeForm.payment_method,
        remarks: payFeeForm.remarks,
        payment_date: payFeeForm.payment_date || todayString(), // Include payment date for backdating
      };
      if (payFeeForm.fee_month && payFeeForm.fee_month.match(/^\d{4}-\d{2}$/)) {
        payload.fee_month = payFeeForm.fee_month;
      }
      const response = await api.post('/fees/collect', payload);
      const monthLabel = payFeeForm.month_label || (payFeeForm.fee_month ? formatMonthYear(payFeeForm.fee_month) : '');
      toast({
        title: 'Success',
        description: `Fee for ${monthLabel || 'selected month'} collected. Receipt: ${response.data.data.receipt_no}`,
      });
      setPayFeeDialogOpen(false);
      setPayFeeForm({
        student_id: '',
        amount: '',
        discount: '0',
        payment_method: 'cash',
        remarks: '',
        month_label: '',
        fee_month: '',
        payment_date: todayString(),
      });
      fetchProfile();
      api.get(`fees/student/${id}/months`)
        .then((res) => setFeeMonths(res.data.data?.months ?? []))
        .catch(() => setFeeMonths([]));
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to collect fee',
        variant: 'destructive',
      });
    }
  };

  const handleOpenReceipt = async (payment) => {
    try {
      await openFeeReceiptPortal(payment, api);
    } catch (error) {
      toast({
        title: 'Could not open receipt',
        description: error?.response?.data?.message || error?.message || 'Try again later',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-border border-t-blue-500" />
      </div>
    );
  }

  if (!profile) return null;

  const {
    student,
    fee_payments = [],
    fee_summary = {},
    fee_calculation = {},
    attendance_summary = {},
    uses_library: profileUsesLibrary = 0,
    uses_transport: profileUsesTransport = 0,
    school_features: profileSchoolFeatures = {},
  } = profile;
  const schoolFeatures = getSchoolFeatures(
    profileSchoolFeatures?.is_library_enabled !== undefined || profileSchoolFeatures?.is_transport_enabled !== undefined
      ? profileSchoolFeatures
      : user,
  );
  const showLibraryToggle = isSchoolFeatureEnabled({ school_features: schoolFeatures }, 'library');
  const showTransportToggle = isSchoolFeatureEnabled({ school_features: schoolFeatures }, 'transport');
  const fullName = [student.first_name, student.last_name].filter(Boolean).join(' ') || 'Student';
  const initials = (student.first_name?.[0] || '') + (student.last_name?.[0] || '') || '?';
  const avatarUrl = getProfileAssetUrl(student.avatar, photoCacheKey);

  const handlePhotoClick = () => {
    if (uploadingPhoto) return;
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image (JPG, PNG, GIF, WebP).', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('photo', prepared);
      const res = await api.post(`students/${id}/photo`, formData);
      const uploadData = res.data?.data ?? {};
      const newAvatar = uploadData.avatar;
      const displayUrl = resolveUploadProfileDisplayUrl(uploadData);
      if (newAvatar) {
        const token = String(Date.now());
        setPhotoCacheKey(token);
        setProfile((prev) => prev ? { ...prev, student: { ...prev.student, avatar: newAvatar } } : null);
        if (!displayUrl && process.env.NODE_ENV === 'development') {
          console.warn('[StudentProfile] Photo saved but display URL missing', uploadData);
        }
        toast({ title: 'Photo updated', description: 'Profile photo has been saved.' });
      }
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.message || 'Could not upload photo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="student-profile-page">
      {/* Back + Edit (when viewing) */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Button
          variant="ghost"
          className="text-muted-foreground hover:text-foreground -ml-2"
          onClick={() => navigate(editMode ? `/students/${id}` : '/students')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {editMode ? 'Cancel (view profile)' : 'Back to Students'}
        </Button>
        {!editMode && (
          <div className="flex items-center gap-2">
            {canManageStudents && isAdmin && (
              <Button
                variant="outline"
                className="border-amber-600 text-amber-400 hover:bg-amber-600 hover:text-foreground"
                onClick={handleResendCredentials}
                disabled={resendingCreds || resendingAdmission || resendingGuardian}
              >
                <Send className="w-4 h-4 mr-2" /> {resendingCreds ? 'Sending…' : 'Resend credentials'}
              </Button>
            )}
            {canManageStudents && (
            <Button
              variant="outline"
              className="border-border text-foreground hover:bg-muted hover:text-foreground"
              onClick={() => navigate(`/students/${id}/edit`)}
            >
              <Edit className="w-4 h-4 mr-2" /> Edit profile
            </Button>
            )}
          </div>
        )}
      </div>

      {/* Header card: photo + main details (or editable when editMode) */}
      <Card className="border-border overflow-hidden">
        <div className="bg-gradient-to-r from-muted to-card h-24" />
        <CardContent className="relative px-6 pb-6">
          <div className="flex flex-col sm:flex-row gap-6 -mt-14">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handlePhotoChange}
            />
            <button
              type="button"
              onClick={handlePhotoClick}
              disabled={uploadingPhoto}
              className="relative h-28 w-28 shrink-0 rounded-xl border-4 border-border shadow-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-background hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <Avatar className="h-full w-full rounded-xl">
                <AvatarImage src={avatarUrl} alt={fullName} className="object-cover" />
                <AvatarFallback className="rounded-xl bg-blue-600/80 text-xl text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-xl">
                <Camera className="w-8 h-8 text-foreground" />
              </span>
              {uploadingPhoto && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
                  <span className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                </span>
              )}
            </button>
            <div className="flex-1 pt-2">
              {editMode && formData ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-muted-foreground">First name</Label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="bg-muted border-border text-foreground"
                      />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Last name</Label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="bg-muted border-border text-foreground"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="bg-muted text-foreground">
                      {student.admission_no} (read-only)
                    </Badge>
                    {student.email && (
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {student.email}
                      </span>
                    )}
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Phone</Label>
                    <MobileInput
                      value={formData.phone}
                      onChange={(v) => {
                        setFormData({ ...formData, phone: v });
                        setPhoneErrors((prev) => ({ ...prev, phone: undefined }));
                      }}
                      error={phoneErrors.phone}
                      className="bg-muted border-border text-foreground"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold text-foreground">{fullName}</h1>
                    <Badge variant="secondary" className="bg-muted text-foreground">
                      {student.admission_no}
                    </Badge>
                    {student.status && (
                      <Badge variant={student.status === 'active' ? 'default' : 'outline'}>
                        {student.status}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-1">
                    {student.class_name || '—'} {student.section_name ? `• ${student.section_name}` : ''}
                    {student.roll_no && ` • Roll ${student.roll_no}`}
                  </p>
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                    {student.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4 text-muted-foreground" /> {student.email}
                      </span>
                    )}
                    {student.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-muted-foreground" /> {student.phone}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {!editMode && (
        <Tabs
          value={profileTab}
          onValueChange={(tab) => {
            setProfileTab(tab);
            if (tab === 'id') {
              const sid = profile?.student?.school_id || user?.school_id;
              fetchSchoolInfo(sid);
            }
          }}
          className="w-full"
        >
          <TabsList className="bg-muted border border-border">
            <TabsTrigger value="overview" className="data-[state=active]:bg-background">
              Overview
            </TabsTrigger>
            <TabsTrigger value="id" className="data-[state=active]:bg-background">
              <IdCard className="w-4 h-4 mr-1.5" />
              ID
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {!editMode && profileTab === 'id' ? (
        <div className="mt-6">
          <StudentIdCard
            student={student}
            school={schoolInfo}
            photoUrl={avatarUrl}
            previewVersion={photoCacheKey || (schoolInfo?.updated_at ? new Date(schoolInfo.updated_at).getTime() : 0)}
            imageCacheKey={
              schoolInfo?.id_card_footer_bg ||
              schoolInfo?.principal_signature ||
              schoolInfo?.logo
                ? String(
                    (schoolInfo?.id_card_footer_bg || schoolInfo?.principal_signature || schoolInfo?.logo || '').match(
                      /_(\d{10,})\./
                    )?.[1] || new Date(schoolInfo?.updated_at || 0).getTime()
                  )
                : ''
            }
          />
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Personal details (editable form when editMode) */}
        <div className="lg:col-span-2 space-y-6">
          {editMode && formData ? (
            <form onSubmit={handleSave} className="space-y-6">
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <User className="w-5 h-5" /> Edit Personal Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className={`grid gap-4 ${editClassUsesSections ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <div>
                      <Label className="text-muted-foreground">Class</Label>
                      <Select value={formData.class_id} onValueChange={(v) => setFormData({ ...formData, class_id: v, section_id: '' })}>
                        <SelectTrigger className="bg-muted border-border text-foreground">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent className="bg-muted border-border">
                          {classes.map((c) => (
                            <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {editClassUsesSections && (
                    <div>
                      <Label className="text-muted-foreground">Section *</Label>
                      <Select value={formData.section_id} onValueChange={(v) => setFormData({ ...formData, section_id: v })} disabled={!formData.class_id}>
                        <SelectTrigger className="bg-muted border-border text-foreground">
                          <SelectValue placeholder="Select section" />
                        </SelectTrigger>
                        <SelectContent className="bg-muted border-border">
                          {sections.map((s) => (
                            <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Roll no</Label>
                      <Input value={formData.roll_no} onChange={(e) => setFormData({ ...formData, roll_no: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Date of birth</Label>
                      <DateInput value={formData.date_of_birth} onChange={(v) => setFormData({ ...formData, date_of_birth: v })} className="bg-muted border-border text-foreground" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Admission date</Label>
                    <DateInput value={formData.admission_date} onChange={(v) => setFormData({ ...formData, admission_date: v })} className="bg-muted border-border text-foreground" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Gender</Label>
                      <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                        <SelectTrigger className="bg-muted border-border text-foreground">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-muted border-border">
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Blood group</Label>
                      <Input value={formData.blood_group} onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })} className="bg-muted border-border text-foreground" placeholder="e.g. A+" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                      <SelectTrigger className="bg-muted border-border text-foreground">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-muted border-border">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Address</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-muted border-border text-foreground" placeholder="Street address" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-muted-foreground">City</Label>
                      <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">State</Label>
                      <Input value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Pincode</Label>
                      <Input value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} className="bg-muted border-border text-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <FileText className="w-5 h-5" /> Documents &amp; IDs
                  </CardTitle>
                  <p className="text-muted-foreground text-sm font-normal">Aadhar, photos, transfer certificate, and other admission documents.</p>
                </CardHeader>
                <CardContent>
                  <StudentAdmissionSections
                    values={formData}
                    onChange={setFormData}
                    pendingUploads={pendingUploads}
                    onPendingUploadsChange={setPendingUploads}
                    existingDocuments={student.documents || []}
                    studentPhotoUrl={avatarUrl}
                    guardianPhotoUrl={student.guardian_photo_url}
                    studentInitials={initials}
                    showAddress={false}
                    onDeleteDocument={handleDeleteDocument}
                    deletingDocId={deletingDocId}
                  />
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <User className="w-5 h-5" /> Parent / Guardian Details
                  </CardTitle>
                  <p className="text-muted-foreground text-sm font-normal">Contact details for school to reach parents or guardian.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">Parent / Guardian name</Label>
                    <Input value={formData.guardian_name} onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })} className="bg-muted border-border text-foreground" placeholder="Full name" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Phone</Label>
                      <MobileInput
                        value={formData.guardian_phone}
                        onChange={(v) => {
                          setFormData({ ...formData, guardian_phone: v });
                          setPhoneErrors((prev) => ({ ...prev, guardian_phone: undefined }));
                        }}
                        error={phoneErrors.guardian_phone}
                        className="bg-muted border-border text-foreground"
                        placeholder="Contact number"
                      />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Email</Label>
                      <Input type="email" value={formData.guardian_email} onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })} className="bg-muted border-border text-foreground" placeholder="Email address" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              {(showLibraryToggle || showTransportToggle) && (
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="text-foreground text-lg">Facilities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <StudentFacilityToggles
                      uses_library={formData.uses_library}
                      uses_transport={formData.uses_transport}
                      schoolFeatures={schoolFeatures}
                      disabled={!canManageStudents}
                      onChange={(field, checked) => setFormData((prev) => ({ ...prev, [field]: checked }))}
                    />
                  </CardContent>
                </Card>
              )}
              <div className="flex gap-3">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save changes'}
                </Button>
                <Button type="button" variant="outline" className="border-border text-foreground" onClick={() => navigate(`/students/${id}`)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <User className="w-5 h-5" /> Personal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <DetailRow label="Date of birth" value={formatDate(student.date_of_birth)} />
              <DetailRow label="Gender" value={student.gender || '—'} />
              <DetailRow label="Blood group" value={student.blood_group || '—'} />
              <DetailRow label="Religion" value={student.religion || '—'} />
              <DetailRow label="Nationality" value={student.nationality || '—'} />
              <DetailRow label="Admission date" value={formatDate(student.admission_date)} />
              {student.address && (
                <div className="flex gap-2 pt-1">
                  <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                  <span className="text-foreground">
                    {[student.address, student.city, student.state, student.pincode].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {student.previous_school && (
                <DetailRow label="Previous school" value={student.previous_school} />
              )}
              {student.aadhar_number && (
                <DetailRow label="Aadhar number" value={student.aadhar_number} />
              )}
            </CardContent>
          </Card>
          )}

          {!editMode && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Documents &amp; IDs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <StudentAdmissionSections
                  values={student}
                  readOnly
                  existingDocuments={student.documents || []}
                  studentPhotoUrl={avatarUrl}
                  guardianPhotoUrl={student.guardian_photo_url}
                  studentInitials={initials}
                  showAddress={false}
                />
              </CardContent>
            </Card>
          )}

          {/* Parent / Guardian (view mode) – always show on profile page */}
          {!editMode && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Users className="w-5 h-5" /> Parent / Guardian
                </CardTitle>
                <p className="text-muted-foreground text-sm font-normal">Contact details for school to reach parents or guardian.</p>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <DetailRow label="Name" value={student.guardian_name || '—'} />
                <div className="flex items-center gap-2 text-foreground">
                  <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span>{student.guardian_phone || '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-foreground">
                  <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span>{student.guardian_email || '—'}</span>
                </div>
                {student.guardian_aadhar_number && (
                  <DetailRow label="Guardian Aadhar" value={student.guardian_aadhar_number} />
                )}
                {student.parent_account_id && (
                  <Link
                    to={`/fees/accounts/${student.parent_account_id}`}
                    className="inline-flex items-center gap-2 text-sm text-blue-500 hover:underline mt-1"
                  >
                    <IndianRupee className="w-4 h-4" />
                    View parent fee account
                  </Link>
                )}
              </CardContent>
            </Card>
          )}

          {!editMode && canManageStudents && isAdmin && (
            <Card className="border-border">
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Mail className="w-5 h-5" /> Admission emails
                  </CardTitle>
                  <p className="text-muted-foreground text-sm font-normal mt-1">Credential delivery to student and guardian inboxes.</p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendAdmissionEmails}
                    disabled={resendingAdmission || resendingGuardian}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    {resendingAdmission ? 'Sending…' : 'Resend all'}
                  </Button>
                  {(student?.guardian_email?.trim() || student?.guardian_phone?.trim()) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResendGuardianEmails}
                      disabled={resendingAdmission || resendingGuardian}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      {resendingGuardian ? 'Sending…' : 'Resend to guardian'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="text-sm">
                {admissionNotifLoading ? (
                  <p className="text-muted-foreground">Loading…</p>
                ) : admissionNotifLogs.length === 0 ? (
                  <p className="text-muted-foreground">No admission email activity yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {admissionNotifLogs.map((log) => (
                      <li key={log.id} className="flex flex-wrap justify-between gap-2 border-b border-border/60 pb-2 last:border-0">
                        <span className="text-foreground">
                          <span className="capitalize">{log.recipient_role || 'recipient'}</span>
                          {' — '}
                          {log.recipient_email}
                        </span>
                        <Badge variant={log.status === 'sent' ? 'default' : 'secondary'} className="capitalize">
                          {log.status}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}

          {!editMode && (showLibraryToggle || showTransportToggle) && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-foreground text-lg">Facilities</CardTitle>
              </CardHeader>
              <CardContent>
                <StudentFacilityToggles
                  uses_library={profileUsesLibrary}
                  uses_transport={profileUsesTransport}
                  schoolFeatures={schoolFeatures}
                  disabled={!canManageStudents}
                  onChange={(field, checked) => handleFacilityToggle(field, checked)}
                />
              </CardContent>
            </Card>
          )}

          {/* Fee payments list */}
          {canViewFees && (
          <>
          <Card className="border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Fee Payments
                </CardTitle>
                {fee_payments.length > 0 && (
                  <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-sm px-3 py-1">
                    {fee_payments.filter(p => p.status === 'paid').length} Paid
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <p className="text-muted-foreground text-sm">
                  Total paid: <span className="text-foreground font-semibold">{formatCurrency(fee_summary.total_paid)}</span>
                </p>
                <Badge variant="outline" className="text-xs border-border text-foreground">
                  {fee_summary.payment_count || 0} payment(s)
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {fee_payments.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4">No fee payments recorded yet.</p>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 text-foreground text-left">
                        <th className="px-4 py-2 font-medium">Receipt</th>
                        <th className="px-4 py-2 font-medium">Fee Month</th>
                        <th className="px-4 py-2 font-medium">Date</th>
                        <th className="px-4 py-2 font-medium">Amount</th>
                        <th className="px-4 py-2 font-medium">Method</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fee_payments.map((p) => {
                        let feeMonthLabel = '—';
                        if (p.fee_month) {
                          try {
                            feeMonthLabel = formatMonthYear(p.fee_month);
                          } catch (e) {
                            feeMonthLabel = p.fee_month;
                          }
                        } else if (p.payment_date) {
                          try {
                            feeMonthLabel = formatMonthYear(p.payment_date);
                          } catch (e) {
                            feeMonthLabel = '—';
                          }
                        }
                        return (
                          <tr key={p.id} className="border-t border-border text-foreground">
                            <td className="px-4 py-2">
                              <button
                                type="button"
                                onClick={() => handleOpenReceipt(p)}
                                className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 hover:underline focus:outline-none focus:underline"
                                title="View receipt"
                              >
                                <FileText className="w-4 h-4 shrink-0" />
                                {p.receipt_no || '—'}
                              </button>
                            </td>
                            <td className="px-4 py-2 font-medium text-blue-300">{feeMonthLabel}</td>
                            <td className="px-4 py-2">{formatDate(p.payment_date)}</td>
                            <td className="px-4 py-2 font-medium text-foreground">
                              {formatCurrency(p.total_amount ?? p.amount)}
                            </td>
                            <td className="px-4 py-2 capitalize">{p.payment_method || '—'}</td>
                            <td className="px-4 py-2">
                              {p.status === 'paid' ? (
                                <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-xs px-2 py-0.5">
                                  Paid
                                </Badge>
                              ) : p.status === 'pending' ? (
                                <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/50 text-xs px-2 py-0.5">
                                  Pending
                                </Badge>
                              ) : p.status === 'cancelled' ? (
                                <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-xs px-2 py-0.5">
                                  Cancelled
                                </Badge>
                              ) : (
                                <Badge className="bg-muted/20 text-muted-foreground border-border/50 text-xs px-2 py-0.5">
                                  {p.status || '—'}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fee by month (due 28th, overdue after 5th next month) */}
          <Card className="border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Fee by month
                </CardTitle>
                {feeMonths && feeMonths.length > 0 && (
                  <div className="flex items-center gap-2">
                    {feeMonths.filter(m => m.status === 'paid').length > 0 && (
                      <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-xs px-2 py-1">
                        {feeMonths.filter(m => m.status === 'paid').length} Paid
                      </Badge>
                    )}
                    {feeMonths.filter(m => m.status === 'overdue').length > 0 && (
                      <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-xs px-2 py-1">
                        {feeMonths.filter(m => m.status === 'overdue').length} Overdue
                      </Badge>
                    )}
                    {feeMonths.filter(m => m.status === 'due').length > 0 && (
                      <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/50 text-xs px-2 py-1">
                        {feeMonths.filter(m => m.status === 'due').length} Due
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2">
                <p className="text-muted-foreground text-sm">
                  Due date: 28th of each month. Overdue if not paid by 5th of next month (compared with admission date).
                </p>
                {feeMonths && feeMonths.length > 0 && (
                  <Badge variant="outline" className="text-xs border-border text-foreground">
                    {feeMonths.length} month(s)
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {feeMonths === null ? (
                <div className="flex justify-center py-6">
                  <span className="animate-spin rounded-full h-6 w-6 border-2 border-border border-t-blue-500" />
                </div>
              ) : feeMonths.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4">No fee months data.</p>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 text-foreground text-left">
                        <th className="px-4 py-2 font-medium">Month</th>
                        <th className="px-4 py-2 font-medium">Due date (28th)</th>
                        <th className="px-4 py-2 font-medium">Overdue from</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeMonths.map((m) => (
                        <tr
                          key={m.year_month}
                          className={`border-t border-border text-foreground ${m.status === 'overdue' || m.status === 'due' ? 'hover:bg-muted/30 cursor-pointer' : ''}`}
                          onClick={() => {
                            if (m.status === 'overdue' || m.status === 'due') {
                              handleOpenPayFee(m);
                            }
                          }}
                        >
                          <td className="px-4 py-2 font-medium">{m.month_label}</td>
                          <td className="px-4 py-2">{m.due_date ? formatDate(m.due_date) : '—'}</td>
                          <td className="px-4 py-2">{m.overdue_from ? formatDate(m.overdue_from) : '—'}</td>
                          <td className="px-4 py-2">
                            {m.status === 'paid' ? (
                              <Badge className="bg-green-600/20 text-green-400 border-green-600/50 text-xs px-2 py-0.5">
                                Paid
                              </Badge>
                            ) : m.status === 'overdue' ? (
                              <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-xs px-2 py-0.5">
                                Overdue
                              </Badge>
                            ) : m.status === 'due' ? (
                              <Badge className="bg-yellow-600/20 text-yellow-400 border-yellow-600/50 text-xs px-2 py-0.5">
                                Due
                              </Badge>
                            ) : (
                              <Badge className="bg-muted/20 text-muted-foreground border-border/50 text-xs px-2 py-0.5">
                                Not Due
                              </Badge>
                            )}
                            {(m.status === 'overdue' || m.status === 'due') && (
                              <span className="ml-2 text-xs text-blue-400">(Click to pay)</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          </>
          )}
        </div>

        {/* Right: Attendance & quick stats */}
        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <ClipboardList className="w-5 h-5" /> Attendance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Average attendance</span>
                  <span className="text-foreground font-semibold">
                    {attendance_summary.average_percent ?? 0}%
                  </span>
                </div>
                <Progress value={attendance_summary.average_percent ?? 0} className="h-2" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <StatPill label="Present" value={attendance_summary.present ?? 0} />
                <StatPill label="Absent" value={attendance_summary.absent ?? 0} />
                <StatPill label="Late" value={attendance_summary.late ?? 0} />
                <StatPill label="Total days" value={attendance_summary.total_days ?? 0} />
              </div>
            </CardContent>
          </Card>

          {canViewFees && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <IndianRupee className="w-5 h-5" /> Fee Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Yearly due</p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(fee_calculation.yearly_total ?? fee_summary.yearly_total)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Paid</p>
                  <p className="font-medium text-green-600">{formatCurrency(fee_calculation.paid ?? fee_summary.total_paid)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Pending</p>
                  <p className="font-medium text-amber-600">{formatCurrency(fee_calculation.pending ?? fee_summary.pending)}</p>
                </div>
              </div>
              {Array.isArray(fee_calculation.breakdown) && fee_calculation.breakdown.length > 0 && (
                <ul className="text-xs space-y-1 border-t border-border pt-2">
                  {fee_calculation.breakdown.filter((b) => b.included !== false).map((b, i) => (
                    <li key={i} className="flex justify-between text-muted-foreground">
                      <span>{b.fee_type_name}</span>
                      <span>{formatCurrency(b.yearly_amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-muted-foreground text-xs">
                {fee_summary.payment_count ?? 0} payment(s) recorded
              </p>
            </CardContent>
          </Card>
          )}
        </div>
      </div>
      )}


      {/* Pay Fee Dialog (opened from overdue/due rows) */}
      {canViewFees && (
      <Dialog open={payFeeDialogOpen} onOpenChange={setPayFeeDialogOpen}>
        <DialogContent className="border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle>Collect Fee - {payFeeForm.month_label}</DialogTitle>
            <p className="text-muted-foreground text-sm font-normal">Pay fee for {student?.first_name} {student?.last_name}</p>
          </DialogHeader>
          <form onSubmit={handlePayFee} className="space-y-4">
            {feeStructure.length > 0 && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="text-foreground font-medium mb-1">Monthly fee breakdown:</p>
                <ul className="space-y-1 text-muted-foreground">
                  {feeStructure
                    .filter((fs) => fs.fee_type_frequency === 'monthly')
                    .map((fs) => (
                      <li key={fs.id} className="flex justify-between">
                        <span>{fs.fee_type_name}</span>
                        <span>₹{Number(fs.amount || 0).toLocaleString()}</span>
                      </li>
                    ))}
                  {feeStructure.filter((fs) => fs.fee_type_frequency === 'monthly').length === 0 && (
                    <li className="text-muted-foreground">No monthly fees defined for this class</li>
                  )}
                </ul>
                {feeStructure.filter((fs) => fs.fee_type_frequency === 'monthly').length > 0 && (
                  <p className="text-foreground font-semibold mt-2 pt-2 border-t border-border flex justify-between">
                    <span>Total monthly fee:</span>
                    <span>₹{calculateMonthlyFeeAmount().toLocaleString()}</span>
                  </p>
                )}
              </div>
            )}
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={payFeeForm.amount}
                onChange={(e) => setPayFeeForm({ ...payFeeForm, amount: e.target.value })}
                className="bg-muted border-border"
                required
                placeholder={feeStructure.length === 0 ? "Enter amount (fee structure not set for this class)" : ""}
              />
              {feeStructure.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  No fee structure found for this class. Set fees in Fee Management → Fee Structure by Class.
                </p>
              )}
            </div>
            <div>
              <Label>Discount (₹)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={payFeeForm.discount}
                onChange={(e) => setPayFeeForm({ ...payFeeForm, discount: e.target.value })}
                className="bg-muted border-border"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select
                value={payFeeForm.payment_method}
                onValueChange={(v) => setPayFeeForm({ ...payFeeForm, payment_method: v })}
              >
                <SelectTrigger className="bg-muted border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Date *</Label>
              <DateInput 
                value={payFeeForm.payment_date || todayString()} 
                onChange={(v) => setPayFeeForm({ ...payFeeForm, payment_date: v })}
                className="bg-muted border-border" 
                required
                max={todayString()}
              />
              <p className="text-muted-foreground text-xs mt-1">
                Select the date when the fee was actually paid. Defaults to today. You can backdate for late entries.
              </p>
            </div>
            <div>
              <Label>Remarks</Label>
              <Input
                value={payFeeForm.remarks}
                onChange={(e) => setPayFeeForm({ ...payFeeForm, remarks: e.target.value })}
                className="bg-muted border-border"
                placeholder="Optional notes"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setPayFeeDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Collect Fee
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
};

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="bg-muted/50 rounded-lg px-3 py-2">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground font-semibold">{value}</p>
    </div>
  );
}

export default StudentProfile;
